import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaWhatsapp, FaRedditAlien, FaYoutube, FaTwitter, FaInstagram, FaFacebookF } from "react-icons/fa";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useSite } from "../context/SiteContext";

const DEFAULT_FOOTER_LINKS = [
  { label: "Contact Us", path: "/contact" },
  { label: "Privacy Policy", path: "/privacy" },
  { label: "Disclaimer", path: "/disclaimer" },
  { label: "Refund Policy", path: "/refund" },
  { label: "Terms & Conditions", path: "/terms-and-conditions" },
  { label: "About", path: "/about" },
];

const Footer = () => {
  const { settings } = useSite();
  const [extraFooterLinks, setExtraFooterLinks] = useState([]);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "navigation"), (snap) => {
      if (snap.exists() && snap.data().footer) {
        setExtraFooterLinks(snap.data().footer);
      }
    });
    return unsub;
  }, []);

  // Merge default + extra (no dupes)
  const footerLinks = [
    ...DEFAULT_FOOTER_LINKS,
    ...extraFooterLinks.filter((l) => !DEFAULT_FOOTER_LINKS.some((d) => d.path === l.path)),
  ];

  const socials = [
    { icon: <FaWhatsapp />, key: "whatsapp" },
    { icon: <FaRedditAlien />, key: "reddit" },
    { icon: <FaYoutube />, key: "youtube" },
    { icon: <FaTwitter />, key: "twitter" },
    { icon: <FaInstagram />, key: "instagram" },
    { icon: <FaFacebookF />, key: "facebook" },
  ];

  return (
    <footer className="bg-white border-t border-[#ececec] w-full" style={{ fontFamily: "Open Sans, sans-serif" }}>
      <div className="max-w-[1300px] mx-auto px-8 lg:px-14 py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">

          {/* Logo + Socials + Links */}
          <div className="md:col-span-3">
            <div className="mb-6">
              <img src={settings.logoDark || settings.logoLight || "/logo.svg"} alt="Simple Kindness"
                className="h-14 w-auto object-contain" onError={(e) => { e.target.style.display = "none"; }} />
            </div>
            <div className="flex items-center gap-4 text-[#8c8c8c] text-[16px] mb-7">
              {socials.map((s) => (
                <a key={s.key} href={settings.social?.[s.key] || "#"} target="_blank" rel="noopener noreferrer"
                  className="hover:text-black transition-colors">{s.icon}</a>
              ))}
            </div>
            {settings.address && <p className="text-[12px] text-[#888] mb-4 leading-relaxed">{settings.address}</p>}
            <ul className="space-y-2.5 text-[13px] text-[#7f7f7f]">
              {footerLinks.map((l) => (
                <li key={l.path} className="flex items-center gap-2 group cursor-pointer">
                  <span className="w-1 h-1 bg-gray-400 rounded-full shrink-0" />
                  <Link to={l.path} className="group-hover:text-black transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* About */}
          <div className="md:col-span-9">
            <h4 className="text-[14px] font-bold uppercase mb-4 text-black">{settings.footerHeading || "About Simple Kindness"}</h4>
            <p className="text-[14px] text-[#888888] leading-[2] text-justify font-light">
              {settings.footerDescription || "Simple Kindness is a mental health resource platform dedicated to breaking the silence and building a compassionate community."}
            </p>

          </div>

        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#e6e6e6] bg-white">
        <div className="max-w-[1300px] mx-auto px-8 lg:px-14 py-5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#9a9a9a] text-[12px] font-medium">
            © {new Date().getFullYear()} Simple Kindness | Made by Upcloud Global Solution
          </p>
          <div className="flex items-center gap-2 opacity-60 grayscale hover:opacity-100 transition-opacity">
            {["PayPal", "VISA", "MASTERCARD", "DISCOVER", "AMEX"].map((p) => (
              <div key={p} className="px-2 py-1 border border-gray-200 rounded text-[10px] font-bold">{p}</div>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
